import { ReactElement } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'

import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { Row } from 'components/v2/Row'
import { Column } from 'components/v2/Column'
import { Input } from 'components/Input'
import { IPlaceCoordinatesForm } from 'features/Places/types'
import { useFormikContext } from 'formik'
import { InputNumber } from 'components/InputNumber'
import { isEmpty, isNull } from 'lodash'
import { GTTPlace } from 'gql/types.generated'

const messages = defineMessages({
  latitudePlaceholder: {
    id: 'places.new.form.latitude.placeholder',
    defaultMessage: 'Latitude'
  },
  longitudePlaceholder: {
    id: 'places.new.form.longitude.placeholder',
    defaultMessage: 'Longitude'
  }
})

interface ICoordinatesFormCardProps {
  place: GTTPlace
}

export const CoordinatesFormCard = ({ place }: ICoordinatesFormCardProps): ReactElement => {
  const intl = useIntl()
  const { values, handleChange, setFieldValue, touched, errors, handleBlur } = useFormikContext<IPlaceCoordinatesForm>()
  const canEditCoordinates = isEmpty(place?.businessCustomer?.id)

  return (
    <FormLayout.Card small>
      <Heading.H4 small>
        <FormattedMessage id="places.form.section.coordinates.title" defaultMessage="Coordinates" />
      </Heading.H4>
      <Row>
        <Column>
          <Input
            id="coordinates.latitude"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.coordinates.latitude}
            placeholder={intl.formatMessage(messages.latitudePlaceholder)}
            touched={touched.coordinates?.latitude}
            errors={errors.coordinates?.latitude}
            label={
              <FormattedMessage
                id="places.new.form.latitude.label"
                defaultMessage="Latitude"
                values={{
                  small: (msg) => <small>{msg}</small>
                }}
              />
            }
            disabled={!canEditCoordinates}
          />
        </Column>
        <Column>
          <Input
            id="coordinates.longitude"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.coordinates.longitude}
            placeholder={intl.formatMessage(messages.longitudePlaceholder)}
            touched={touched.coordinates?.longitude}
            errors={errors.coordinates?.longitude}
            label={
              <FormattedMessage
                id="places.new.form.longitude.label"
                defaultMessage="Longitude"
                values={{
                  small: (msg) => <small>{msg}</small>
                }}
              />
            }
            disabled={!canEditCoordinates}
          />
        </Column>
      </Row>
      <Row>
        <Column width="50%">
          <InputNumber
            id="geofenceRadius"
            onChange={(value: string): void => {
              setFieldValue('geofenceRadius', value ? parseInt(value) : null)
            }}
            onBlur={handleBlur}
            value={!isNull(values.geofenceRadius) ? values.geofenceRadius?.toString() : ''}
            label={<FormattedMessage id="places.new.form.geofenceRadius.label" defaultMessage="Geofence radius" />}
            placeholder="100m"
            maskOptions={{
              suffix: 'm',
              allowDecimal: false
            }}
            touched={touched.geofenceRadius}
            errors={errors.geofenceRadius}
            optional
          />
        </Column>
      </Row>
    </FormLayout.Card>
  )
}
